import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage, Language } from '../context/LanguageContext';
import { getLocalizedEquivalentUrl } from '../lib/routes';
import { Globe, Check, ChevronDown } from 'lucide-react';

interface LanguageSelectorProps {
  variant?: 'header' | 'mobile' | 'pill';
  className?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'header',
  className = '',
}) => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Derive active language synchronously from URL subfolder first to prevent mixed-language rendering
  const pathLower = (location.pathname || '/').toLowerCase();
  const activeLang: Language =
    pathLower === '/fr' || pathLower.startsWith('/fr/')
      ? 'fr'
      : pathLower === '/en' || pathLower.startsWith('/en/')
      ? 'en'
      : language;

  const getHrefForLang = (lang: Language) => {
    const localizedPath = getLocalizedEquivalentUrl(location.pathname, lang, location.search);
    return `${localizedPath}${location.hash}`;
  };

  const languages: { code: Language; label: string; shortLabel: string; flag: string; hreflang: string }[] = [
    { code: 'en', label: 'English', shortLabel: 'EN', flag: '🇬🇧', hreflang: 'en-cm' },
    { code: 'fr', label: 'Français', shortLabel: 'FR', flag: '🇫🇷', hreflang: 'fr-cm' },
  ];

  const currentLang = languages.find((l) => l.code === activeLang) || languages[0];

  if (variant === 'pill') {
    return (
      <div className={`inline-flex items-center p-1 rounded-full bg-slate-100 border border-slate-200 shadow-2xs ${className}`}>
        {languages.map((l) => {
          const isCurrent = activeLang === l.code;
          return (
            <Link
              key={l.code}
              to={getHrefForLang(l.code)}
              hrefLang={l.hreflang}
              lang={l.code}
              onClick={() => setLanguage(l.code)}
              aria-current={isCurrent ? 'true' : undefined}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                isCurrent
                  ? 'bg-[#094426] text-white shadow-2xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span>{l.shortLabel}</span>
            </Link>
          );
        })}
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className={`flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200 ${className}`}>
        <div className="flex items-center gap-2 text-slate-700">
          <Globe className="w-4 h-4 text-[#094426]" />
          <span className="text-xs font-bold text-slate-900">{t.language}:</span>
        </div>
        <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200">
          {languages.map((l) => {
            const isCurrent = activeLang === l.code;
            return (
              <Link
                key={l.code}
                to={getHrefForLang(l.code)}
                hrefLang={l.hreflang}
                lang={l.code}
                onClick={() => setLanguage(l.code)}
                aria-current={isCurrent ? 'true' : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  isCurrent
                    ? 'bg-[#094426] text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <span>{l.flag}</span>
                <span>{l.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // Compact Header Dropdown showing current language (🌐 EN ⌄ on /en, 🌐 FR ⌄ on /fr)
  // Menu links remain mounted in the DOM as crawlable <a href="..."> anchors for SEO bots.
  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        id="language-selector-button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
        className="group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-slate-200/90 bg-slate-50/70 hover:bg-white hover:border-slate-300 text-slate-700 transition-all duration-150 cursor-pointer shadow-2xs active:scale-95"
        aria-label={activeLang === 'fr' ? 'Changer de langue : Français' : 'Select language: English'}
        title={activeLang === 'fr' ? 'Changer de langue (FR / EN)' : 'Change language (EN / FR)'}
      >
        <Globe className="w-4 h-4 text-emerald-800 group-hover:rotate-12 transition-transform duration-300 stroke-[1.9]" />
        <span className="text-xs font-extrabold text-slate-800 tracking-wider uppercase font-mono">
          {currentLang.shortLabel}
        </span>
        <ChevronDown
          className={`w-3 h-3 text-slate-400 group-hover:text-slate-600 transition-transform duration-200 ${
            dropdownOpen ? 'rotate-180 text-emerald-800' : ''
          }`}
        />
      </button>

      <div
        className={`absolute right-0 mt-2 w-44 bg-white/98 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200/90 p-1.5 z-50 transition-all duration-150 ${
          dropdownOpen
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
      >
        <div className="px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 border-b border-slate-100 flex items-center gap-1.5">
          <Globe className="w-3 h-3 text-emerald-700" />
          <span>{t.language}</span>
        </div>
        <div className="pt-1 space-y-0.5">
          {languages.map((l) => {
            const isSelected = activeLang === l.code;
            return (
              <Link
                key={l.code}
                id={`lang-select-${l.code}`}
                to={getHrefForLang(l.code)}
                hrefLang={l.hreflang}
                lang={l.code}
                aria-current={isSelected ? 'true' : undefined}
                onClick={() => {
                  setDropdownOpen(false);
                  setLanguage(l.code);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs text-left transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-emerald-50 text-[#094426] font-bold'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{l.flag}</span>
                  <span>{l.label}</span>
                </div>
                {isSelected && (
                  <div className="w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <Check className="w-2.5 h-2.5 stroke-[3]" />
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};
